// frontend/src/components/Leave/ProvisionalHospitalisationModal.js
import React, { useEffect, useMemo, useState } from 'react';
import { HeartPulse, Info } from 'lucide-react';
import Modal from '../UI/Modal';
import SearchableCombobox from '../UI/SearchableCombobox';
import DatePickerField from '../UI/DatePickerField';
import FormSelect from '../UI/FormSelect';
import { useStaffList } from '../../hooks/useStaffData';
import { useDebounce } from '../../hooks/useDebounce';
import {
  useCreateProvisionalHospitalisation,
  useExtendProvisionalHospitalisation,
} from '../../hooks/useLeaveData';

const REPORT_SOURCES = [
  { value: 'family', label: 'Family' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'employee', label: 'Employee' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'other', label: 'Other' },
];

const toLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const parts = String(value).slice(0, 10).split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

const toIsoDate = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : toLocalDate(value);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDays = (value, days) => {
  const d = toLocalDate(value);
  if (!d) return null;
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
};

const ProvisionalHospitalisationModal = ({
  isOpen,
  onClose,
  onSuccess,
  prefill = null,
  extendApplication = null,
}) => {
  const isExtend = Boolean(extendApplication?.id);
  const [staffId, setStaffId] = useState('');
  const [search, setSearch] = useState('');
  const [reportedStart, setReportedStart] = useState(null);
  const [paidThrough, setPaidThrough] = useState(null);
  const [nextReview, setNextReview] = useState(null);
  const [reportSource, setReportSource] = useState('family');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState('');

  const debouncedSearch = useDebounce(search, 300);
  const { data: staffData } = useStaffList({
    search: debouncedSearch.trim().length >= 2 ? debouncedSearch : undefined,
    page_size: 10,
    employment_status: 'active',
  }, { enabled: isOpen && !isExtend && !prefill?.staffId });
  const staffList = useMemo(() => staffData?.results || [], [staffData]);

  const createMutation = useCreateProvisionalHospitalisation();
  const extendMutation = useExtendProvisionalHospitalisation();
  const pending = createMutation.isPending || extendMutation.isPending;

  useEffect(() => {
    if (!isOpen) return;
    setFormError('');
    if (isExtend) {
      setStaffId(extendApplication.staff_id || extendApplication.staff_code || '');
      setReportedStart(toLocalDate(
        extendApplication.hospitalisation_reported_start || extendApplication.leave_start_date,
      ));
      setPaidThrough(toLocalDate(
        extendApplication.hospitalisation_paid_through || extendApplication.leave_end_date,
      ));
      setNextReview(toLocalDate(extendApplication.hospitalisation_next_review_date));
      setReportSource(extendApplication.hospitalisation_report_source || 'other');
      setNotes('');
      return;
    }
    const start = toLocalDate(prefill?.date);
    setStaffId(prefill?.staffId || '');
    setSearch(prefill?.employeeName || '');
    setReportedStart(start);
    setPaidThrough(start ? addDays(start, 3) : null);
    setNextReview(start ? addDays(start, 7) : null);
    setReportSource('family');
    setNotes('');
  }, [isOpen, isExtend, extendApplication, prefill]);

  const selectedStaffLabel = useMemo(() => {
    if (prefill?.employeeName) return prefill.employeeName;
    if (extendApplication?.employee_name) return extendApplication.employee_name;
    const hit = staffList.find((s) => s.staff_id === staffId);
    if (!hit) return staffId;
    return `${hit.first_name || ''} ${hit.last_name || ''}`.trim() || hit.staff_id;
  }, [prefill, extendApplication, staffList, staffId]);

  const handleClose = () => {
    if (pending) return;
    onClose?.();
  };

  const handleSubmit = () => {
    setFormError('');
    if (!isExtend && !staffId) {
      setFormError('Select an employee.');
      return;
    }
    if (!reportedStart || !paidThrough || !nextReview) {
      setFormError('First hospitalised date, paid-through date, and next review date are required.');
      return;
    }
    if (paidThrough < reportedStart) {
      setFormError('Paid-through date cannot be before the first hospitalised date.');
      return;
    }
    if (!notes.trim()) {
      setFormError('A non-clinical note is required.');
      return;
    }
    const onDone = {
      onSuccess: () => {
        onSuccess?.();
        onClose?.();
      },
    };
    if (isExtend) {
      extendMutation.mutate({
        applicationId: extendApplication.id,
        data: {
          paid_through: toIsoDate(paidThrough),
          next_review_date: toIsoDate(nextReview),
          notes: notes.trim(),
        },
      }, onDone);
      return;
    }
    createMutation.mutate({
      staff_id: staffId,
      reported_start: toIsoDate(reportedStart),
      paid_through: toIsoDate(paidThrough),
      next_review_date: toIsoDate(nextReview),
      report_source: reportSource,
      notes: notes.trim(),
    }, onDone);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isExtend ? 'Extend provisional hospitalisation' : 'Record hospitalisation'}
      size="md"
      theme="admin"
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={handleClose} className="btn btn-admin-outline" disabled={pending}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={pending}
            className="btn btn-admin-primary"
          >
            <HeartPulse className="w-4 h-4 mr-2" />
            {pending ? 'Saving...' : (isExtend ? 'Update coverage' : 'Record hospitalisation')}
          </button>
        </>
      }
    >
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
        <div className="flex items-start gap-2 text-sm text-amber-900">
          <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Paid provisionally — certificate pending</p>
            <p className="text-xs text-amber-800 mt-1">
              Attendance and payroll stay paid through the date you set. Hospitalisation leave
              balance is not consumed until the medical certificate is verified.
            </p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Employee <span className="text-red-600">*</span>
        </label>
        {isExtend || prefill?.staffId ? (
          <div className="input-admin flex items-center text-sm text-gray-900">
            {selectedStaffLabel}{staffId ? ` (${staffId})` : ''}
          </div>
        ) : (
          <SearchableCombobox
            items={staffList}
            value={staffId}
            onChange={setStaffId}
            onSearch={setSearch}
            displayFn={(s) => `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.staff_id}
            valueFn={(s) => s.staff_id}
            searchFn={() => true}
            placeholder="Search staff..."
            searchPlaceholder="Type name or staff ID..."
            variant="form"
            theme="admin"
            className="w-full"
          />
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="prov-hl-start" className="block text-sm font-medium text-gray-700 mb-1">
            First hospitalised / absent date <span className="text-red-600">*</span>
          </label>
          <DatePickerField
            id="prov-hl-start"
            selected={reportedStart}
            onChange={setReportedStart}
            theme="admin"
            placeholderText="Select start date"
            disabled={isExtend}
          />
        </div>
        <div>
          <label htmlFor="prov-hl-through" className="block text-sm font-medium text-gray-700 mb-1">
            Paid through <span className="text-red-600">*</span>
          </label>
          <DatePickerField
            id="prov-hl-through"
            selected={paidThrough}
            onChange={setPaidThrough}
            theme="admin"
            placeholderText="Select paid-through date"
            minDate={reportedStart}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="prov-hl-review" className="block text-sm font-medium text-gray-700 mb-1">
            Next review date <span className="text-red-600">*</span>
          </label>
          <DatePickerField
            id="prov-hl-review"
            selected={nextReview}
            onChange={setNextReview}
            theme="admin"
            placeholderText="Select review date"
          />
        </div>
        {!isExtend && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report source <span className="text-red-600">*</span>
            </label>
            <FormSelect
              options={REPORT_SOURCES}
              value={reportSource}
              onChange={setReportSource}
              theme="admin"
              required
            />
          </div>
        )}
      </div>

      <div>
        <label htmlFor="prov-hl-notes" className="block text-sm font-medium text-gray-700 mb-1">
          Non-clinical note <span className="text-red-600">*</span>
        </label>
        <textarea
          id="prov-hl-notes"
          rows={3}
          className="textarea-admin"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Who reported this, and any operational follow-up. Do not record diagnosis."
        />
      </div>

      {formError && (
        <p className="text-sm text-red-700">{formError}</p>
      )}
    </Modal>
  );
};

export default ProvisionalHospitalisationModal;
